import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type FC,
  type ReactNode,
} from "react";
import type { LastBotCore } from "../core/LastBotCore";
import type { CoreEvents } from "../core/events";
import type { LayoutHint, Message } from "../core/types";
import {
  activeLayoutHintForMessages,
  activeSubstantiveAssistant,
  normalizeHintLength,
} from "../components/chatWidgetLayout";

const RICH_CONTENT_LAYOUT_HINT: Required<LayoutHint> = {
  min_width: "34rem",
  min_height: "780px",
};

/**
 * How long a settled, media-free answer must hold the previous rich size before
 * the popup shrinks back. Long enough that a metadata refresh or a follow-up
 * rich answer arriving right behind it does not produce a shrink→grow flap.
 */
export const RICH_MEDIA_SHRINK_DELAY_MS = 600;

/** The subset of the size payload this provider ever owns. */
interface OwnedSize {
  width?: string | null;
  maxHeight?: string | null;
}

/** The full `widget:set-size` payload, as an embedder may send it. */
type SetSizePayload = CoreEvents["widget:set-size"];

interface RichMediaReporter {
  reportMedia: () => void;
  reportSettled: () => void;
}

interface RichMediaOwner {
  reportMedia: (threadId: number, messageId: number) => void;
  reportSettled: (threadId: number, messageId: number) => void;
}

const noop = (): void => undefined;

const defaultReporter: RichMediaReporter = {
  reportMedia: noop,
  reportSettled: noop,
};

const defaultOwner: RichMediaOwner = {
  reportMedia: (_threadId: number, _messageId: number): void => undefined,
  reportSettled: (_threadId: number, _messageId: number): void => undefined,
};

const RichMediaOwnerContext = createContext<RichMediaOwner>(defaultOwner);
export const RichMediaContext = createContext<RichMediaReporter>(defaultReporter);

export const useRichMedia = (): RichMediaReporter => useContext(RichMediaContext);

interface RichMediaScopeProps {
  threadId: number;
  messageId: number;
  settled: boolean;
  enabled?: boolean;
  children: ReactNode;
}

/**
 * Binds renderer reports to the message and thread that produced this render.
 * A stale passive effect can therefore never resize a subsequently opened thread.
 */
export const RichMediaScope: FC<RichMediaScopeProps> = ({
  threadId,
  messageId,
  settled,
  enabled = true,
  children,
}) => {
  const owner = useContext(RichMediaOwnerContext);
  const reporter = useMemo<RichMediaReporter>(() => ({
    reportMedia: (): void => {
      if (enabled) owner.reportMedia(threadId, messageId);
    },
    reportSettled: (): void => {
      if (enabled) owner.reportSettled(threadId, messageId);
    },
  }), [enabled, messageId, owner, threadId]);

  useEffect(() => {
    if (settled) reporter.reportSettled();
  }, [reporter, settled]);

  return (
    <RichMediaContext.Provider value={reporter}>
      {children}
    </RichMediaContext.Provider>
  );
};

interface ReportState {
  threadId: number | null;
  messageId: number | null;
  hasMedia: boolean;
  settled: boolean;
}

interface RichMediaProviderProps {
  core: LastBotCore;
  threadId: number | null;
  messages: Message[];
  isOpen: boolean;
  isFullscreen: boolean;
  inline: boolean;
  children: ReactNode;
}

const supportsLength = (property: "width" | "max-height", value: string): boolean =>
  typeof CSS === "undefined" || CSS.supports(property, value);

/**
 * Owns rich-answer popup sizing for the whole App lifetime, including while the
 * panel is closed. An embedder's first setSize call permanently opts it out.
 */
export const RichMediaProvider: FC<RichMediaProviderProps> = ({
  core,
  threadId,
  messages,
  isOpen,
  isFullscreen,
  inline,
  children,
}) => {
  const [reportState, setReportState] = useState<ReportState>({
    threadId: null,
    messageId: null,
    hasMedia: false,
    settled: false,
  });
  const [externalSizingDisabled, setExternalSizingDisabled] = useState(false);
  const currentThreadIdRef = useRef<number | null>(threadId);
  // Holds the exact payload object handed to core.setSize for the duration of
  // that call. Self-recognition is by identity rather than a boolean flag: an
  // embedder that reacts to widget:set-size by calling setSize again (clamping,
  // say) dispatches a DIFFERENT object while our own call is still on the
  // stack, and a boolean would swallow that nested event as internal.
  const emittedSizeRef = useRef<OwnedSize | null>(null);
  const externalSizingDisabledRef = useRef(false);
  const appliedRef = useRef({ width: false, maxHeight: false });
  const appliedTargetRef = useRef<string | null>(null);
  const previousThreadIdRef = useRef<number | null>(threadId);
  const shrinkTimerRef = useRef<number | null>(null);
  // A provider's first owned size emission records the current revision. Until
  // then, any revision proves an embedder called public setSize before React
  // mounted (the custom element exposes core before root.render()). App keeps
  // this provider mounted for the lifetime of a live core; a future remount
  // against a surviving core must reconcile this invariant explicitly.
  const observedSizeRevisionRef = useRef(0);
  const selectedMessage = activeSubstantiveAssistant(messages);
  // ChatMessageList excludes null contents, so a non-rendered reply cannot
  // settle this controller after a thread switch.
  const activeMessage = selectedMessage?.contents === null ? null : selectedMessage;
  const hasUnrenderedActiveMessage = selectedMessage?.contents === null;
  const activeMessageId = activeMessage?.id ?? null;
  const activeMessageIdRef = useRef<number | null>(activeMessageId);

  // Commit the thread/message identity before any passive renderer reports run.
  // Unlike a render-phase assignment, this cannot expose an abandoned concurrent
  // render as the current thread.
  useLayoutEffect(() => {
    currentThreadIdRef.current = threadId;
    activeMessageIdRef.current = activeMessageId;
  }, [activeMessageId, threadId]);

  const reportMedia = useCallback((reportedThreadId: number, messageId: number): void => {
    if (
      currentThreadIdRef.current !== reportedThreadId ||
      activeMessageIdRef.current !== messageId
    ) return;
    setReportState((previous) => {
      if (previous.threadId !== reportedThreadId || previous.messageId !== messageId) {
        return {
          threadId: reportedThreadId,
          messageId,
          hasMedia: true,
          settled: false,
        };
      }
      return previous.hasMedia ? previous : { ...previous, hasMedia: true };
    });
  }, []);

  const reportSettled = useCallback((reportedThreadId: number, messageId: number): void => {
    if (
      currentThreadIdRef.current !== reportedThreadId ||
      activeMessageIdRef.current !== messageId
    ) return;
    setReportState((previous) => {
      if (previous.threadId !== reportedThreadId || previous.messageId !== messageId) {
        return {
          threadId: reportedThreadId,
          messageId,
          hasMedia: false,
          settled: true,
        };
      }
      return previous.settled ? previous : { ...previous, settled: true };
    });
  }, []);

  const owner = useMemo<RichMediaOwner>(
    // The object identity deliberately changes with the active reply so mounted
    // renderer effects report again once a streaming reply becomes eligible.
    () => ({
      reportMedia: (reportedThreadId: number, messageId: number): void => {
        if (messageId === activeMessageId) reportMedia(reportedThreadId, messageId);
      },
      reportSettled: (reportedThreadId: number, messageId: number): void => {
        if (messageId === activeMessageId) reportSettled(reportedThreadId, messageId);
      },
    }),
    [activeMessageId, reportMedia, reportSettled],
  );

  const emitSize = useCallback((size: OwnedSize): void => {
    const previous = emittedSizeRef.current;
    emittedSizeRef.current = size;
    try {
      core.setSize(size);
      // Read live: a nested embedder call dispatched during our own emission
      // bumps the revision again, and the mount-time reconciliation must not
      // then mistake that for a pre-mount external call.
      observedSizeRevisionRef.current = core._getSizeRevision();
    } finally {
      // Restore rather than clear: resetOwnedSize can re-enter emitSize from
      // inside a relinquish triggered by our own dispatch.
      emittedSizeRef.current = previous;
    }
  }, [core]);

  /**
   * Releases the width/max-height this provider applied. `preserve` carries the
   * external payload that triggered the release, if any: a key the embedder just
   * set (to a length OR to `null`) is its decision and is left untouched, while a
   * key it omitted (`undefined`) still belongs to us and is reset to its default.
   */
  const resetOwnedSize = useCallback((preserve?: SetSizePayload): void => {
    const applied = appliedRef.current;
    if (!applied.width && !applied.maxHeight) return;
    const size: OwnedSize = {
      width: applied.width && preserve?.width === undefined ? null : undefined,
      maxHeight: applied.maxHeight && preserve?.maxHeight === undefined ? null : undefined,
    };
    // Drop ownership before emitting so a re-entrant path cannot reset twice.
    appliedRef.current = { width: false, maxHeight: false };
    appliedTargetRef.current = null;
    if (size.width === undefined && size.maxHeight === undefined) return;
    emitSize(size);
  }, [emitSize]);

  const relinquishSizeOwnership = useCallback((externalSize?: SetSizePayload): void => {
    if (externalSizingDisabledRef.current) return;
    // Do this before latching ownership so an embedder's own height/width stays
    // intact while our temporary width/max-height vars are released.
    resetOwnedSize(externalSize);
    externalSizingDisabledRef.current = true;
    setExternalSizingDisabled(true);
    observedSizeRevisionRef.current = core._getSizeRevision();
  }, [core, resetOwnedSize]);

  useEffect(() => {
    // core is exposed before React effects subscribe. A revision mismatch means
    // an embedder already called setSize and owns this instance permanently.
    if (core._getSizeRevision() !== observedSizeRevisionRef.current) {
      relinquishSizeOwnership();
    }
    const stopListening = core.on("widget:set-size", (size) => {
      // Identity, not a flag: only the exact object we are currently emitting
      // is ours. Anything else — including a nested call an embedder makes
      // from its own widget:set-size handler — hands ownership over.
      if (size === emittedSizeRef.current) return;
      relinquishSizeOwnership(size);
    });
    return stopListening;
  }, [core, relinquishSizeOwnership]);

  const hasFreshReports =
    reportState.threadId === threadId && reportState.messageId === activeMessageId;
  const activeHasMedia = activeMessage != null &&
    hasFreshReports && reportState.hasMedia;
  const activeSettled = activeMessage != null &&
    hasFreshReports && reportState.settled;
  const layoutHint = activeMessage === null ? null : activeLayoutHintForMessages(messages);

  useEffect(() => {
    const clearShrink = (): void => {
      if (shrinkTimerRef.current !== null) {
        window.clearTimeout(shrinkTimerRef.current);
        shrinkTimerRef.current = null;
      }
    };
    const threadChanged = previousThreadIdRef.current !== threadId;
    previousThreadIdRef.current = threadId;

    clearShrink();

    if (externalSizingDisabledRef.current || externalSizingDisabled) return clearShrink;

    if (!isOpen || isFullscreen) {
      resetOwnedSize();
      return clearShrink;
    }

    const shouldGrow = layoutHint !== null || activeHasMedia;
    if (shouldGrow) {
      const target = {
        width: inline ? undefined : normalizeHintLength(
          layoutHint === null ? RICH_CONTENT_LAYOUT_HINT.min_width : layoutHint.min_width,
        ),
        maxHeight: normalizeHintLength(
          layoutHint === null ? RICH_CONTENT_LAYOUT_HINT.min_height : layoutHint.min_height,
        ),
      };
      if (target.width !== undefined && !supportsLength("width", target.width)) target.width = undefined;
      if (target.maxHeight !== undefined && !supportsLength("max-height", target.maxHeight)) target.maxHeight = undefined;
      const targetKey = `${target.width ?? ""}|${target.maxHeight ?? ""}`;
      if ((target.width !== undefined || target.maxHeight !== undefined) && appliedTargetRef.current !== targetKey) {
        // Record ownership BEFORE emitting: an embedder can relinquish us from
        // inside its own handler for this very event, and the reset that runs
        // then must already know which keys we are about to own.
        appliedRef.current = {
          width: appliedRef.current.width || target.width !== undefined,
          maxHeight: appliedRef.current.maxHeight || target.maxHeight !== undefined,
        };
        appliedTargetRef.current = targetKey;
        emitSize(target);
      }
      return clearShrink;
    }

    // A user-only history thread has no scope that could settle the decision.
    // For an assistant reply, hold the old rich size until its renderer reports
    // media or its settled text-only state, avoiding a shrink→grow thread flap.
    if (threadChanged && activeMessage === null) {
      resetOwnedSize();
      return clearShrink;
    }

    // Keep the previous rich size while the next answer is streaming. Only its
    // own settled, media-free render may shrink the popup back to its defaults.
    if (activeSettled || hasUnrenderedActiveMessage) {
      shrinkTimerRef.current = window.setTimeout(() => {
        shrinkTimerRef.current = null;
        resetOwnedSize();
      }, RICH_MEDIA_SHRINK_DELAY_MS);
    }
    return clearShrink;
  }, [
    activeMessage,
    activeHasMedia,
    activeSettled,
    emitSize,
    externalSizingDisabled,
    inline,
    isFullscreen,
    isOpen,
    layoutHint,
    resetOwnedSize,
    hasUnrenderedActiveMessage,
    threadId,
  ]);

  return (
    <RichMediaOwnerContext.Provider value={owner}>
      {children}
    </RichMediaOwnerContext.Provider>
  );
};
